import imageCompression from 'browser-image-compression';

export interface CompressionOptions {
  maxSizeMB: number;
  maxWidthOrHeight: number;
  useWebWorker: boolean;
  quality: number;
}

export const DEFAULT_AVATAR_OPTIONS: CompressionOptions = {
  maxSizeMB: 0.5, // 500KB limit for avatars
  maxWidthOrHeight: 512, // Max 512px for avatars
  useWebWorker: false, // Disabled due to CSP restrictions
  quality: 0.8
};

export const DEFAULT_BACKGROUND_OPTIONS: CompressionOptions = {
  maxSizeMB: 2, // 2MB limit for backgrounds
  maxWidthOrHeight: 1920, // Max 1920px for backgrounds
  useWebWorker: false, // Disabled due to CSP restrictions
  quality: 0.85
};

/**
 * Compresses an image file using browser-image-compression
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Promise<File> - The compressed image file
 */
export const compressImage = async (
  file: File,
  options: CompressionOptions = DEFAULT_AVATAR_OPTIONS
): Promise<File> => {
  try {
    console.log('Image compression started:', {
      originalName: file.name,
      originalSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
      options
    });

    const compressedBlob = await imageCompression(file, {
      maxSizeMB: options.maxSizeMB,
      maxWidthOrHeight: options.maxWidthOrHeight,
      useWebWorker: options.useWebWorker,
      initialQuality: options.quality
    });

    console.log('Image compression completed:', {
      originalSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
      compressedSize: `${(compressedBlob.size / 1024 / 1024).toFixed(2)} MB`,
      compressionRatio: `${((1 - compressedBlob.size / file.size) * 100).toFixed(1)}%`
    });

    // Create new File object preserving original filename and mimetype (critical for multer validation)
    const compressedFile = new File([compressedBlob], file.name, {
      type: file.type,
      lastModified: Date.now()
    });

    return compressedFile;
  } catch (error) {
    console.error('Image compression failed:', error);
    console.warn('Falling back to original file');
    return file; // Return original file if compression fails
  }
};

/**
 * Compresses avatar images with avatar-specific settings
 */
export const compressAvatarImage = (file: File): Promise<File> => {
  return compressImage(file, DEFAULT_AVATAR_OPTIONS);
};

/**
 * Compresses background images with background-specific settings
 */
export const compressBackgroundImage = (file: File): Promise<File> => {
  return compressImage(file, DEFAULT_BACKGROUND_OPTIONS);
};

/**
 * Validates if a file is a valid image type
 */
export const validateImageFile = (file: File): boolean => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  return validTypes.includes(file.type.toLowerCase());
};

/**
 * Gets human-readable file size
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};